import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ReportSummary } from '../lib/api';
import { DesktopOnlyNotice, EmptyState, Money, Spinner, StatusPill } from '../components/ui';

/** Desktop-first: this is a queue people work through at a desk. */
export function ReviewQueuePage() {
  const [reports, setReports] = useState<ReportSummary[] | null>(null);

  useEffect(() => {
    api.get<{ reports: ReportSummary[] }>('/reports?scope=queue')
      .then((d) => setReports(d.reports))
      .catch(() => setReports([]));
  }, []);

  if (!reports) return <Spinner label="Loading the review queue" />;

  return (
    <div>
      <h1 className="mb-4 text-xl font-semibold">Review queue</h1>
      <DesktopOnlyNotice />

      {reports.length === 0 ? (
        <EmptyState title="Nothing waiting" hint="Submitted reports appear here." />
      ) : (
        <>
          <ul className="space-y-3 lg:hidden">
            {reports.map((r) => (
              <li key={r.id}>
                <Link to={`/reports/${r.id}`} className="card block p-4">
                  <p className="font-medium">{r.title}</p>
                  <p className="text-sm text-slate-500">{r.ownerName}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <StatusPill status={r.status} />
                    <Money value={r.submittedTotalAmount ?? r.currentTotal} className="font-semibold" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          <div className="card hidden overflow-hidden lg:block">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="px-4 py-2 font-medium">Submitter</th>
                  <th className="px-4 py-2 font-medium">Report</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Period</th>
                  <th className="px-4 py-2 font-medium">Submitted</th>
                  <th className="px-4 py-2 font-medium">Age</th>
                  <th className="px-4 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {reports.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">{r.ownerName}</td>
                    <td className="px-4 py-2">
                      <Link to={`/reports/${r.id}`} className="font-medium text-brand hover:underline">{r.title}</Link>
                    </td>
                    <td className="px-4 py-2 capitalize">{r.type}</td>
                    <td className="px-4 py-2">{r.type === 'travel' ? r.tripDestination : r.periodMonth?.slice(0, 7)}</td>
                    <td className="px-4 py-2">{r.submittedAt?.slice(0, 10)}</td>
                    <td className="px-4 py-2">{ageInDays(r.submittedAt)}</td>
                    <td className="px-4 py-2 text-right">
                      <Money value={r.submittedTotalAmount ?? r.currentTotal} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function ageInDays(iso: string | null): string {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  return days <= 0 ? 'today' : `${days}d`;
}
